import i18nInst from 'src/i18n';
import {
  addEmails,
  checkActionByType,
  checkDisClaimer,
  checkEmailFormatAndEmpty,
  checkMaxLength,
  checkNotification,
  checkSuspiciousTag,
  removeBlockedHeader,
  removeHeader,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
} from 'src/pages/Policy/utils/fns';
import {
  ActionEnum,
  AntiSpamFilter,
  ApprovedHeader,
  AsActionKeys,
  AsActionToDisclaimerMap,
  AsNotifyTypeKeys,
  AsSuspiciousTagKeys,
  EnableDisableEnum,
  ExchangeOnlinePolicyItem,
  Notification,
  PolicyFilters,
} from 'src/pages/Policy/utils/types';
import {
  asActionDataSelector,
  asApprovedHeaderListSelector,
  asBlockedHeaderListSelector,
} from './selectors';
import { BlockedHeaderRuleDefinitionsProps } from '@Components/BlockedHeaderFieldList';

/* eslint-disable @typescript-eslint/no-unused-vars */
export const createAntiSpamSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  return {
    setAsState: (payload: Partial<AntiSpamFilter>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          ...payload,
        },
      }));
    },
    setAsNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          notification: {
            ...state.tmAntispamfilters.notification,
            ...payload,
          },
        },
      }));
    },
    asActionHandler: (key: AsActionKeys) => (action: ActionEnum) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          [key]: action,
        },
      }));
    },
    asNotifyTypeHandler: (key: AsNotifyTypeKeys) => {
      const currentState = getPolicy();
      const originValue = currentState.tmAntispamfilters[key];
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          [key]: originValue ? 0 : 1,
        },
      }));
    },
    asSuspiciousTagHandler: (key: AsSuspiciousTagKeys) => (value: string) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          [key]: value,
        },
      }));
    },
    importAsApprovedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmAntispamfilters: { asApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importEmails = emails.filter((email) => email.length > 0);
      const separator = ';';
      const existedEntries = asApprovedSenderEmailEntries
        .split(separator)
        .filter((entry: string) => entry.length > 0);
      if (
        importEmails.length + existedEntries.length >
        limitation.asApprovedSenderMaxCount
      ) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.asApprovedSenderMaxCount
        );
      } else {
        currentStore.addAsApprovedSender(importEmails);
      }
    },
    addAsApprovedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmAntispamfilters: { asApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { singleApprovedListMaxCount },
      } = currentStore;
      const includeDomain = true;
      const { entriesStr, errList } = addEmails(
        email,
        asApprovedSenderEmailEntries,
        includeDomain,
        singleApprovedListMaxCount,
        ';',
        -1,
        true
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asApprovedSenderEmailEntries: entriesStr,
        },
      }));
      // process error list
      if (errList.length) {
        console.log('addAsApprovedSender error list:', errList);
        window.showNotification({
          titleMsg: t('policy.approvedList.errorAdd'),
          bodyMsg:
            errList.length > 50
              ? `${errList.slice(0, 50).join('\n')}\n...`
              : errList.join('\n'),
          severity: 'error',
        });
      }
    },
    removeAsApprovedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmAntispamfilters: { asApprovedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        asApprovedSenderEmailEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asApprovedSenderEmailEntries: result,
        },
      }));
    },
    importAsBlockedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmAntispamfilters: { asBlockedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importEmails = emails.filter((email) => email.length > 0);
      const separator = ';';
      const existedEntries = asBlockedSenderEmailEntries
        .split(separator)
        .filter((entry: string) => entry.length > 0);
      if (
        importEmails.length + existedEntries.length >
        limitation.blockedListMaxCount
      ) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.blockedListMaxCount
        );
      } else {
        currentStore.addAsBlockedSender(importEmails);
      }
    },
    addAsBlockedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmAntispamfilters: { asBlockedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const includeDomain = true;
      const { entriesStr, errList } = addEmails(
        email,
        asBlockedSenderEmailEntries,
        includeDomain,
        limitation.singleBlockedListMaxCount,
        ';',
        -1,
        true
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asBlockedSenderEmailEntries: entriesStr,
        },
      }));
      // process error list
      if (errList.length) {
        console.log('addAsBlockedSender error list:', errList);
        window.showNotification({
          titleMsg: t('policy.blockedList.errorAdd'),
          bodyMsg:
            errList.length > 50
              ? `${errList.slice(0, 50).join('\n')}\n...`
              : errList.join('\n'),
          severity: 'error',
        });
      }
    },
    removeAsBlockedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmAntispamfilters: { asBlockedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        asBlockedSenderEmailEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asBlockedSenderEmailEntries: result,
        },
      }));
    },
    addAsApprovedHeader: (header: ApprovedHeader) => {
      const currentStore = getAll();
      const existedEntries = asApprovedHeaderListSelector(currentStore);
      existedEntries.push(header);
      // set state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asApprovedHeaderList: JSON.stringify(existedEntries),
        },
      }));
    },
    removeAsApprovedHeader: (header: ApprovedHeader) => {
      const currentStore = getAll();
      const existedEntries = asApprovedHeaderListSelector(currentStore);
      const newEntries = removeHeader(header, existedEntries);
      // set state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asApprovedHeaderList: JSON.stringify(newEntries),
        },
      }));
    },
    addAsBlockedHeader: (header: BlockedHeaderRuleDefinitionsProps[]) => {
      const currentStore = getAll();
      const existedEntries = asBlockedHeaderListSelector(currentStore);
      existedEntries.push(header);
      // set state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asBlockedHeaderRuleList: JSON.stringify(existedEntries),
        },
      }));
    },
    removeAsBlockedHeader: (header: BlockedHeaderRuleDefinitionsProps[]) => {
      const currentStore = getAll();
      const existedEntries = asBlockedHeaderListSelector(currentStore);
      const newEntries = removeBlockedHeader(header, existedEntries);
      // set state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmAntispamfilters: {
          ...state.tmAntispamfilters,
          asBlockedHeaderRuleList: JSON.stringify(newEntries),
        },
      }));
    },
    validateAntispam: () => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const filter = currentState.tmAntispamfilters;
      const actionData = asActionDataSelector(currentStore);
      currentStore.replaceInvalidAction(actionData);

      const result = {
        [PolicyFilters.tmAntispamfilters]: {},
      };
      const actionKeys = Object.values(AsActionKeys);
      const suspiciousTagKeys = Object.values(AsSuspiciousTagKeys);
      const {
        disclaimerMaxLength,
        notifyMessageMaxLength,
        notifySubjectMaxLength,
      } = currentStore.config.limitation;
      const invalidEmailMsgObj = {
        title: t('policy.error.invalidAdminEmailTitle'),
        content: t('policy.error.invalidAdminEmailContent'),
      };
      const invalidMessageObj = {
        title: t('policy.error.invalidMessageTitle'),
        content: t('policy.error.invalidMessageContent', {
          limit: notifyMessageMaxLength,
        }),
      };
      const invalidSubjectObj = {
        title: t('policy.error.invalidSubjectTitle'),
        content: t('policy.error.invalidSubjectContent', {
          limit: notifySubjectMaxLength,
        }),
      };
      // check action
      for (let i = 0; i < actionKeys.length; i++) {
        const key = actionKeys[i];
        const actionType = filter[key];
        if (actionType === ActionEnum.tagSubject) {
          // check suspicious tag
          const suspiciousTagValue = filter[suspiciousTagKeys[i]];
          const checkResult = checkActionByType(suspiciousTagValue, actionType);
          if (checkResult) {
            // return directly when meet an error
            result[PolicyFilters.tmAntispamfilters] = {
              [suspiciousTagKeys[i]]: checkResult,
            };
            return result;
          }
        }
        if (
          actionType === ActionEnum.addDisclaimer &&
          key !== AsActionKeys.asActionBlockedSender
        ) {
          // check add disclaimer
          const addDisclaimerKey = AsActionToDisclaimerMap[key];
          const addDisclaimerValue = filter[addDisclaimerKey];
          const checkResult = checkActionByType(
            addDisclaimerValue,
            actionType,
            disclaimerMaxLength
          );
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              [addDisclaimerKey]: checkResult,
            };
            return result;
          }
        }
      }
      // check writing style
      if (filter.wsEnabled === EnableDisableEnum.enabled) {
        // check suspicious tag
        let checkResult;
        if (filter.wsNotifyRecipientActionType === ActionEnum.tagSubject) {
          checkResult = checkSuspiciousTag(filter.wsSuspiciousTag);
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              wsSuspiciousTag: checkResult,
            };
            return result;
          }
        }
        // check add disclaimer
        if (filter.wsNotifyRecipientActionType === ActionEnum.addDisclaimer) {
          checkResult = checkDisClaimer(
            filter.wsDisclaimer,
            disclaimerMaxLength
          );
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              wsDisclaimer: checkResult,
            };
            return result;
          }
        }
        // check notify administator
        if (filter.wsNotifyAdministratorEnabled === EnableDisableEnum.enabled) {
          // email
          let checkResult = checkEmailFormatAndEmpty(
            filter.ntfWritingStyleToAdminAccount,
            invalidEmailMsgObj
          );
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              ntfWritingStyleToAdminAccount: checkResult,
            };
            return result;
          }

          // subject
          checkResult = checkMaxLength(
            filter.ntfWritingStyleAdministratorSubject,
            notifySubjectMaxLength,
            invalidSubjectObj
          );
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              ntfWritingStyleAdministratorSubject: checkResult,
            };
            return result;
          }
          // message
          checkResult = checkMaxLength(
            filter.ntfWritingStyleAdministratorMessage,
            notifyMessageMaxLength,
            invalidMessageObj
          );
          if (checkResult) {
            result[PolicyFilters.tmAntispamfilters] = {
              ntfWritingStyleAdministratorMessage: checkResult,
            };
            return result;
          }
        }
      }
      // check notification
      const checkResult = checkNotification(
        filter.notification,
        notifyMessageMaxLength,
        notifySubjectMaxLength
      );
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmAntispamfilters] = checkResult;
        return result;
      }
      return;
    },
  };
};
